import { Link } from "react-router";
import { CheckCircle, Circle } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { ModuleSetupStepStatus } from "@/modules/apps/useModuleSetup";

/** Steps to get a module working, each with a link to where it's done. */
export const ModuleSetupChecklist = ({
  steps,
  isPending,
  onNavigate,
}: {
  steps: ModuleSetupStepStatus[];
  isPending?: boolean;
  /** Called before following a step link (e.g. to close a dialog). */
  onNavigate?: () => void;
}) => {
  if (isPending) {
    return (
      <div className="flex flex-col gap-2">
        {steps.map((step) => (
          <Skeleton key={step.id} className="h-16 rounded-lg" />
        ))}
      </div>
    );
  }
  return (
    <ol className="flex flex-col gap-2">
      {steps.map((step) => {
        const done = step.done === true;
        return (
          <li
            key={step.id}
            className={cn(
              "flex items-center gap-3 rounded-lg border px-3 py-3",
              done ? "bg-muted/40" : "bg-card",
            )}
          >
            {done ? (
              <CheckCircle
                weight="fill"
                className="size-5 shrink-0 text-success"
              />
            ) : (
              <Circle className="size-5 shrink-0 text-muted-foreground" />
            )}
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  "flex items-center gap-2 text-sm font-medium",
                  done && "text-muted-foreground line-through",
                )}
              >
                {step.title}
                {step.optional ? <Badge variant="muted">Optional</Badge> : null}
              </p>
              <p className="text-xs text-muted-foreground">
                {step.description}
              </p>
            </div>
            {done ? null : (
              <Button asChild size="sm" variant="outline">
                <Link to={step.to} onClick={onNavigate}>
                  {step.actionLabel}
                </Link>
              </Button>
            )}
          </li>
        );
      })}
    </ol>
  );
};
